import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { Box, Flex, Input, Portal, Spinner, Text } from '@chakra-ui/react';
import ConfirmationDialog from '@/components/util-component/Dialog/ConfirmationDialog';
import useNetwork from '@/hooks/network/useNetwork';
import { useNetworkEditable } from '@/hooks/network/useNetworkEditable';
import { useRunningOptimizations } from '@/hooks/network/useRunningOptimizations';
import useVariableOptimizationJob from '@/hooks/network/useVariableOptimizationJob';
import { OptimizationOptions } from '@/services/bayesianApi/optimizationModels';
import { defaultOptionsForJIT } from '@/utils/optimization/optimizationDefaults';
import OptimizeMenu, { OPTIMIZATION_TIME_BUFFER_MINUTES, optimizationPresets, OptimizationPresetId } from './OptimizeMenu';
import { OptimizationDialog } from '../../pages/Digraph/Dialogs/OptimizationDialog';
type PresetConfig = {
  label: string;
  maxMinutes: number;
};
const presetConfigById = Object.fromEntries(optimizationPresets.map(preset => [preset.id, {
  label: preset.label,
  maxMinutes: preset.maxMinutes
}])) as Record<OptimizationPresetId, PresetConfig>;
const formatMinutes = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const remainderMinutes = minutes % 60;
  if (remainderMinutes === 0) {
    return `${hours} hours`;
  }
  return `${hours} hours ${remainderMinutes} minutes`;
};
const formatEstimatedRange = (minutes: number): string => {
  const maxMinutes = minutes + OPTIMIZATION_TIME_BUFFER_MINUTES;
  const minIsHours = minutes >= 60;
  const maxIsHours = maxMinutes >= 60;
  if (!minIsHours && !maxIsHours) {
    return `${minutes}-${maxMinutes} minutes`;
  }
  return `${formatMinutes(minutes)}-${formatMinutes(maxMinutes)}`;
};
type OptimizeMenuContainerProps = {
  networkId: string;
  trigger?: import('react').ReactElement;
};
const OptimizeMenuContainer = t0 => {
  const $ = _c(58);
  const {
    networkId,
    trigger
  } = t0;
  let t1;
  if ($[0] !== networkId) {
    t1 = {
      networkId
    };
    $[0] = networkId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    network,
    isLoading
  } = useNetwork(t1);
  const {
    isReadOnly
  } = useNetworkEditable(networkId);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      hideLoadingToast: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    mutate: optimizationWithJob,
    isPending: isStartingOptimization
  } = useVariableOptimizationJob(networkId, t2);
  const {
    data: runningOptimizations
  } = useRunningOptimizations(networkId);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [jobName, setJobName] = useState("");
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  let t3;
  if ($[3] !== network) {
    t3 = network ? Array.from(network.variableById.keys()) : [];
    $[3] = network;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const allVariableIds = t3;
  let t4;
  if ($[5] !== network) {
    t4 = network ? Array.from(network.variableById.values()).map(_temp).filter(_temp2) : [];
    $[5] = network;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const allVariableNames = t4;
  const isMenuDisabled = isLoading || isReadOnly || allVariableIds.length === 0;
  const presetConfig = selectedPresetId ? presetConfigById[selectedPresetId] : null;
  const runningOptimizationCount = runningOptimizations?.length ?? 0;
  let t5;
  if ($[7] !== network?.name || $[8] !== runningOptimizationCount) {
    t5 = presetId => {
      if (runningOptimizationCount > 0) {
        setPendingAction(presetId);
        setIsWarningDialogOpen(true);
        return;
      }
      setSelectedPresetId(presetId);
      setJobName(network?.name ?? "");
    };
    $[7] = network?.name;
    $[8] = runningOptimizationCount;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSelectPreset = t5;
  let t6;
  if ($[10] !== network?.name || $[11] !== runningOptimizationCount) {
    t6 = () => {
      if (runningOptimizationCount > 0) {
        setPendingAction("custom");
        setIsWarningDialogOpen(true);
        return;
      }
      setJobName(network?.name ?? "");
      setIsCustomDialogOpen(true);
    };
    $[10] = network?.name;
    $[11] = runningOptimizationCount;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSelectCustom = t6;
  let t7;
  if ($[13] !== network?.name || $[14] !== pendingAction) {
    t7 = () => {
      setIsWarningDialogOpen(false);
      if (pendingAction === "custom") {
        setJobName(network?.name ?? "");
        setIsCustomDialogOpen(true);
      } else {
        if (pendingAction) {
          setSelectedPresetId(pendingAction);
          setJobName(network?.name ?? "");
        }
      }
      setPendingAction(null);
    };
    $[13] = network?.name;
    $[14] = pendingAction;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleProceedAfterWarning = t7;
  let t8;
  if ($[16] !== allVariableIds || $[17] !== jobName || $[18] !== optimizationWithJob || $[19] !== presetConfig) {
    t8 = () => {
      if (!presetConfig || allVariableIds.length === 0) {
        return;
      }
      const options = {
        ...defaultOptionsForJIT,
        max_time_minutes: presetConfig.maxMinutes
      };
      optimizationWithJob({
        selectedVariables: new Set(allVariableIds),
        options,
        jobName: jobName.trim()
      });
    };
    $[16] = allVariableIds;
    $[17] = jobName;
    $[18] = optimizationWithJob;
    $[19] = presetConfig;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleConfirmPreset = t8;
  let t9;
  if ($[21] !== jobName) {
    t9 = jobName.trim();
    $[21] = jobName;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const isJobNameValid = t9.length > 0;
  let t10;
  if ($[23] !== handleSelectCustom || $[24] !== handleSelectPreset || $[25] !== isMenuDisabled || $[26] !== trigger) {
    t10 = <OptimizeMenu isDisabled={isMenuDisabled} trigger={trigger} onSelectCustom={handleSelectCustom} onSelectPreset={handleSelectPreset} />;
    $[23] = handleSelectCustom;
    $[24] = handleSelectPreset;
    $[25] = isMenuDisabled;
    $[26] = trigger;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== allVariableIds.length || $[29] !== handleConfirmPreset || $[30] !== isJobNameValid || $[31] !== jobName || $[32] !== presetConfig || $[33] !== selectedPresetId) {
    t11 = presetConfig && <ConfirmationDialog confirmingActionText="Optimize" description={`This will start a ${presetConfig.label.toLowerCase()} optimization for ${allVariableIds.length} variables with an estimated runtime of about ${formatEstimatedRange(presetConfig.maxMinutes)}.`} isConfirmable={isJobNameValid} isOpen={!!selectedPresetId} title={`Optimize (${presetConfig.label})?`} onClose={() => setSelectedPresetId(null)} onConfirm={handleConfirmPreset}><Box><label>Job Name:</label><Input value={jobName} onBlur={() => setJobName(jobName.trim())} onChange={e => setJobName(e.target.value)} />{!isJobNameValid && <Text color="red.500" fontSize="sm" mt={1}>Job name is required</Text>}</Box></ConfirmationDialog>;
    $[28] = allVariableIds.length;
    $[29] = handleConfirmPreset;
    $[30] = isJobNameValid;
    $[31] = jobName;
    $[32] = presetConfig;
    $[33] = selectedPresetId;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsCustomDialogOpen(false);
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== allVariableIds || $[37] !== optimizationWithJob) {
    t13 = (options_0, name_0) => {
      optimizationWithJob({
        selectedVariables: new Set(allVariableIds),
        options: options_0,
        jobName: name_0.trim()
      });
    };
    $[36] = allVariableIds;
    $[37] = optimizationWithJob;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== allVariableIds.length || $[40] !== allVariableNames || $[41] !== isCustomDialogOpen || $[42] !== jobName || $[43] !== t13) {
    t14 = <OptimizationDialog isOpen={isCustomDialogOpen} jobName={jobName} selectedNodeCount={allVariableIds.length} selectedNodeNames={allVariableNames} setJobName={setJobName} onClose={t12} onConfirm={t13} />;
    $[39] = allVariableIds.length;
    $[40] = allVariableNames;
    $[41] = isCustomDialogOpen;
    $[42] = jobName;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const t15 = `There ${runningOptimizationCount === 1 ? "is" : "are"} already ${runningOptimizationCount} optimization${runningOptimizationCount === 1 ? "" : "s"} running for this network. Starting another optimization could produce inaccurate results.`;
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setIsWarningDialogOpen(false);
      setPendingAction(null);
    };
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== handleProceedAfterWarning || $[47] !== isWarningDialogOpen || $[48] !== t15) {
    t17 = <ConfirmationDialog confirmingActionText="Proceed" description={t15} isOpen={isWarningDialogOpen} title="Optimization already running" onClose={t16} onConfirm={handleProceedAfterWarning} />;
    $[46] = handleProceedAfterWarning;
    $[47] = isWarningDialogOpen;
    $[48] = t15;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== isStartingOptimization) {
    t18 = isStartingOptimization && <Portal><Box alignItems="center" bg="blackAlpha.700" bottom={0} display="flex" justifyContent="center" left={0} position="fixed" right={0} top={0} zIndex={1400}><Flex alignItems="center" bg="white" borderRadius="xl" direction="column" gap={4} p={10} shadow="xl"><Spinner color="blue.500" size="xl" /><Text fontWeight="medium">Starting optimization…</Text></Flex></Box></Portal>;
    $[50] = isStartingOptimization;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t10 || $[53] !== t11 || $[54] !== t14 || $[55] !== t17 || $[56] !== t18) {
    t19 = <>{t10}{t11}{t14}{t17}{t18}</>;
    $[52] = t10;
    $[53] = t11;
    $[54] = t14;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  return t19;
};
export default OptimizeMenuContainer;
function _temp(variable) {
  return variable.name;
}
function _temp2(name) {
  return name && name.length > 0;
}